import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const Container = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 2rem auto;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 3rem;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 2rem;

  span {
    color: #007bff;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeadingSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  cursor: pointer;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const HeadingItem = styled.div`
  background-color: ${props => (props.active ? '#e6f2ff' : '#f8f9fa')};
  border: 2px solid ${props => (props.active ? '#007bff' : 'transparent')};
  padding: 1.5rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: ${props => (props.active ? '#007bff' : '#555')};
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: ${props => (props.active ? '0 2px 8px rgba(0, 123, 255, 0.2)' : 'none')};

  &:hover {
    background-color: #e6f2ff;
    border-color: #007bff;
    color: #007bff;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1rem;
  }
`;

const ContentSection = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: nowrap;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const DetailsBox = styled.div`
  flex: 2;
  background-color: #c8fefdff;
  padding: 2rem;
  border-radius: 8px;
  border: 2px solid #36aaa8;
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ResultsBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 250px;
  background-color: #bdb024

  @media (max-width: 992px) {
    min-width: unset;
  }
`;

const ResultItem = styled.div`
  background-color: #ffc107;
  color: #333;
  padding: 1.2rem 1.5rem;
  border-radius: 8px;
  font-weight: bold;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: -1rem;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 1rem;
    background-color: #ffc107;
  }

  @media (max-width: 992px) {
    &:not(:last-child)::after {
      content: none;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem 1.2rem;
  }
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    color: #555;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const data = {
  'Boost Website Ranking': {
    details: [
      'Custom SEO Solutions',
      'Local SEO',
      'Ecommerce SEO Services',
      'SEO Consultancy',
      'Guest Post Services',
      'Web analytics',
      'SEO Audit',
    ],
    results: [
      'Google #1 Ranking For Targeted Keywords',
      'No.1 In Google Map With Your City',
      'Outrank Your Toughest Competitors',
      'Boosts Your Credibility',
      'Decreases Advertising Costs',
    ],
  },
  'Quick Leads with Paid Ads': {
    details: [
      'PPC Campaign Management',
      'Google Ads & Bing Ads',
      'Social Media Advertising',
      'Retargeting Campaigns',
      'Conversion Rate Optimization',
      'A/B Testing',
    ],
    results: [
      'High-Quality Leads',
      'Increased Sales',
      'Immediate Visibility',
      'Targeted Audience Reach',
      'Measurable ROI',
    ],
  },
  'Digital Branding': {
    details: [
      'Brand Strategy',
      'Logo & Visual Identity',
      'Social Media Management',
      'Content Marketing',
      'Public Relations',
      'Email Marketing',
    ],
    results: [
      'Strong Brand Recognition',
      'Improved Customer Loyalty',
      'Market Authority',
      'Enhanced Reputation',
      'Increased Brand Value',
    ],
  },
  SEO: {
    details: [
      'On-Page SEO',
      'Off-Page SEO',
      'Technical SEO',
      'Keyword Research',
      'Competitor Analysis',
      'Link Building',
    ],
    results: [
      'Organic Traffic Growth',
      'High Search Engine Rankings',
      'Improved Website Health',
      'Long-Term Results',
      'Cost-Effective Marketing',
    ],
  },
};

const MarketingComponent = () => {
  const [activeHeading, setActiveHeading] = useState('Boost Website Ranking');

  const currentData = data[activeHeading];

  return (
    <Container>
      <Title>How <span>Digital Marketing Services</span> help you?</Title>
      <HeadingSection>
        {Object.keys(data).map(heading => (
          <HeadingItem
            key={heading}
            active={activeHeading === heading}
            onClick={() => setActiveHeading(heading)}
          >
            {heading}
          </HeadingItem>
        ))}
      </HeadingSection>
      <ContentSection>
        <DetailsBox>
          <DetailsList>
            {currentData.details.map((detail, index) => (
              <li key={index}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#007bff' }} />
                {detail}
              </li>
            ))}
          </DetailsList>
        </DetailsBox>
        <ResultsBox>
          <ResultItem>RESULTS WILL BE</ResultItem>
          {currentData.results.map((result, index) => (
            <ResultItem key={index}>{result}</ResultItem>
          ))}
        </ResultsBox>
      </ContentSection>
    </Container>
  );
};

export default MarketingComponent;